import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FlyingRobots from '../components/FlyingRobots';
import {
  ArrowLeft, Warehouse, PackageCheck, Truck, ClipboardList,
  AlertTriangle, CheckCircle2, Clock, Radar, Boxes, MapPin, Store, Utensils,
} from 'lucide-react';

const StatCard = ({ label, value, detail, icon: Icon, highlight }) => (
  <div
    className={`p-4 rounded-lg border ${
      highlight ? 'border-[#10b981]/30 bg-[#10b981]/5' : 'border-white/10 bg-white/[0.02]'
    }`}
  >
    <div className="flex items-start justify-between mb-2">
      <p className="text-white/60 text-[13px] font-medium">{label}</p>
      <Icon size={16} className={highlight ? 'text-[#10b981]' : 'text-white/40'} />
    </div>
    <p className="h-sans text-[28px] font-medium mb-1">{value}</p>
    {detail && <p className="text-white/50 text-[12px]">{detail}</p>}
  </div>
);

const STEPS = [
  {
    icon: Radar,
    title: 'Track stock at every branch',
    body: 'Every SKU at every branch has a live qty_on_hand, checked against a par level set for that store.',
  },
  {
    icon: AlertTriangle,
    title: 'Detect what’s below par',
    body: 'Anything at or under its reorder point is flagged automatically — no manager has to notice or ask.',
  },
  {
    icon: ClipboardList,
    title: 'Auto-generate today’s shipment',
    body: 'One manifest per branch is built from the warehouse’s available stock, topping each SKU back up to par.',
  },
  {
    icon: Truck,
    title: 'Dispatch & confirm delivery',
    body: 'Shipments move Preparing → Packed → In Transit → Delivered, and branch stock updates the moment it arrives.',
  },
];

const BRANCHES = [
  { name: 'Total Buah Kelapa Gading', city: 'Jakarta Utara', skusLow: 2, availability: 97, lastShipment: 'Today, 09:12' },
  { name: 'Total Buah Kemang', city: 'Jakarta Selatan', skusLow: 4, availability: 92, lastShipment: 'Today, 08:47' },
  { name: 'Total Buah BSD City', city: 'Tangerang Selatan', skusLow: 1, availability: 98, lastShipment: 'Today, 09:31' },
  { name: 'Total Buah Bekasi', city: 'Jawa Barat', skusLow: 6, availability: 88, lastShipment: 'Today, 08:52' },
  { name: 'Total Buah Depok', city: 'Jawa Barat', skusLow: 0, availability: 100, lastShipment: 'Today, 09:05' },
  { name: 'Total Buah Bogor', city: 'Jawa Barat', skusLow: 3, availability: 95, lastShipment: 'Today, 09:18' },
];

const branchStatus = (skusLow) => {
  if (skusLow === 0) return { label: 'Healthy', color: '#10b981' };
  if (skusLow <= 3) return { label: 'Low stock', color: '#f59e0b' };
  return { label: 'Critical', color: '#ef4444' };
};

const SHIPMENTS = [
  { branch: 'Total Buah Bekasi', items: 6, units: 214, status: 'In Transit', dispatched: '07:02', eta: '10:15' },
  { branch: 'Total Buah Kemang', items: 4, units: 138, status: 'In Transit', dispatched: '07:04', eta: '09:50' },
  { branch: 'Total Buah Bogor', items: 3, units: 96, status: 'Packed', dispatched: '—', eta: '10:40' },
  { branch: 'Total Buah Kelapa Gading', items: 2, units: 64, status: 'Delivered', dispatched: '06:51', eta: '09:12' },
  { branch: 'Total Buah BSD City', items: 1, units: 22, status: 'Delivered', dispatched: '07:10', eta: '09:31' },
];

const STATUS_STEPS = ['Preparing', 'Packed', 'In Transit', 'Delivered'];

const StatusPipeline = ({ status }) => {
  const idx = STATUS_STEPS.indexOf(status);
  return (
    <div className="flex items-center gap-1.5">
      {STATUS_STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ background: i <= idx ? '#10b981' : 'rgba(255,255,255,0.15)' }}
          />
          {i < STATUS_STEPS.length - 1 && (
            <div
              className="w-4 h-px flex-shrink-0"
              style={{ background: i < idx ? '#10b981' : 'rgba(255,255,255,0.15)' }}
            />
          )}
        </React.Fragment>
      ))}
      <span className="ml-2 text-[12px] text-white/70 whitespace-nowrap">{status}</span>
    </div>
  );
};

export default function WarehouseOrdering() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#050506] text-white relative min-h-screen">
      <FlyingRobots />
      <Navbar />

      <main className="pt-[100px] pb-20">
        <div className="mx-auto max-w-[1400px] px-6">
          {/* Header */}
          <div className="mb-12">
            <Link
              to="/tbs-dashboard"
              className="inline-flex items-center gap-1.5 text-white/50 hover:text-white/80 text-[13px] mb-6 transition-colors"
            >
              <ArrowLeft size={14} /> Back to dashboard
            </Link>
            <p className="eyebrow text-white/50 mb-2">CASE STUDY &middot; WAREHOUSE ORDERING</p>
            <h1 className="h-display text-[44px] md:text-[52px] leading-[1.1] max-w-[820px]">
              One warehouse, six branches, replenished automatically — every day.
            </h1>
            <p className="text-white/60 text-[16px] mt-4 max-w-[640px]">
              Grocera tracks live stock at every Total Buah Segar branch and ships a
              top-up from the central warehouse the moment any SKU falls below its
              par level. No manager places the order.
            </p>
          </div>

          {/* Vertical applicability */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-14">
            <div className="card-line p-5 flex items-start gap-4">
              <div className="w-9 h-9 rounded-md flex items-center justify-center bg-[#10b981]/10 border border-[#10b981]/30 flex-shrink-0">
                <Store size={16} className="text-[#10b981]" />
              </div>
              <div>
                <p className="h-sans text-[15px] mb-1">Grocery &middot; live pilot</p>
                <p className="text-white/60 text-[13px] leading-[1.6]">
                  Total Buah Segar — produce and packaged SKUs, warehouse &rarr; branch,
                  shown throughout this page.
                </p>
              </div>
            </div>
            <div className="card-line p-5 flex items-start gap-4">
              <div className="w-9 h-9 rounded-md flex items-center justify-center bg-[#2E6BFF]/10 border border-[#2E6BFF]/30 flex-shrink-0">
                <Utensils size={16} className="text-[#2E6BFF]" />
              </div>
              <div>
                <p className="h-sans text-[15px] mb-1">QSR &middot; same engine</p>
                <p className="text-white/60 text-[13px] leading-[1.6]">
                  Quick service restaurant chains run the identical loop &mdash;
                  commissary &rarr; outlet, tighter par levels for faster ingredient turnover.
                </p>
              </div>
            </div>
          </div>

          {/* KPI Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-14">
            <StatCard label="Branches Auto-Replenished Daily" value="6 / 6" detail="Every TBS branch, every morning" icon={Warehouse} highlight />
            <StatCard label="Stockouts Prevented (30d)" value="47" detail="SKUs that would have hit zero" icon={AlertTriangle} highlight />
            <StatCard label="Avg. Replenishment Cycle" value="4.2h" detail="Dispatch to delivered" icon={Clock} />
            <StatCard label="On-Time Delivery Rate" value="98%" detail="Shipments arriving by ETA" icon={PackageCheck} highlight />
          </div>

          {/* How it works */}
          <div className="mb-14">
            <p className="eyebrow text-white/50 mb-6">HOW IT WORKS</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {STEPS.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <div key={step.title} className="card-line p-5 relative">
                    <div className="mono text-[10.5px] text-white/40 uppercase tracking-widest mb-4">
                      Step {idx + 1}
                    </div>
                    <div className="w-9 h-9 rounded-md flex items-center justify-center mb-4 bg-[#10b981]/10 border border-[#10b981]/30">
                      <Icon size={16} className="text-[#10b981]" />
                    </div>
                    <h3 className="h-sans text-white text-[16px] mb-2 leading-[1.3]">{step.title}</h3>
                    <p className="text-white/60 text-[13px] leading-[1.6]">{step.body}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Warehouse + schedule row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8 mb-14">
            <div className="lg:col-span-1 border border-white/10 rounded-lg bg-white/[0.02] p-6">
              <div className="flex items-center gap-2 mb-4">
                <Boxes size={16} className="text-[#10b981]" />
                <h2 className="h-sans text-[18px] font-medium">Grocera Central &middot; Cikarang</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-white/50 text-[12px] mb-1">SKUs tracked</p>
                  <p className="h-sans text-[24px] font-medium">29</p>
                </div>
                <div>
                  <p className="text-white/50 text-[12px] mb-1">Units in stock</p>
                  <p className="h-sans text-[24px] font-medium">18,640</p>
                </div>
                <div>
                  <p className="text-white/50 text-[12px] mb-1">Low-stock alerts today</p>
                  <p className="h-sans text-[24px] font-medium text-orange-400">16</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 border border-white/10 rounded-lg bg-white/[0.02] p-6">
              <div className="flex items-center gap-2 mb-6">
                <Clock size={16} className="text-[#10b981]" />
                <h2 className="h-sans text-[18px] font-medium">Today&rsquo;s replenishment run</h2>
              </div>
              <div className="relative pl-2">
                <div className="absolute left-2 top-2 right-2 h-px bg-white/10 hidden md:block" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {[
                    { time: '05:00', label: 'Stock check', body: 'Every branch’s qty_on_hand compared to par level' },
                    { time: '05:30', label: 'Shipments generated', body: '5 manifests built from warehouse availability' },
                    { time: '07:00', label: 'Dispatched', body: 'Trucks leave the warehouse for each branch' },
                    { time: '10:00', label: 'Delivered', body: 'All branches topped up before the lunch rush' },
                  ].map((s) => (
                    <div key={s.time} className="relative">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#10b981] mb-3 relative z-10" />
                      <p className="mono text-[11px] text-[#10b981] mb-1">{s.time}</p>
                      <p className="text-white text-[13px] font-medium mb-1">{s.label}</p>
                      <p className="text-white/50 text-[12px] leading-[1.5]">{s.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Branch stock table */}
          <div className="border border-white/10 rounded-lg bg-white/[0.02] overflow-hidden mb-14">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <MapPin size={16} className="text-[#10b981]" />
                <h2 className="h-sans text-[20px] font-medium">Branch stock health</h2>
              </div>
              <p className="text-white/60 text-[13px]">Live availability vs. par level, by branch</p>
            </div>
            <div className="divide-y divide-white/5">
              {BRANCHES.map((b) => {
                const status = branchStatus(b.skusLow);
                return (
                  <div key={b.name} className="p-6 hover:bg-white/[0.02] transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
                      <div>
                        <p className="font-medium">{b.name}</p>
                        <p className="text-white/40 text-[12px]">{b.city}</p>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-[12px] text-white/50 mb-1">SKUs below par</p>
                          <p className="h-sans text-[18px] font-medium" style={{ color: b.skusLow > 3 ? '#ef4444' : b.skusLow > 0 ? '#f59e0b' : '#10b981' }}>
                            {b.skusLow}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[12px] text-white/50 mb-1">Last shipment</p>
                          <p className="text-[13px]">{b.lastShipment}</p>
                        </div>
                        <span
                          className="inline-block px-2.5 py-1 rounded text-[11px] font-medium flex-shrink-0"
                          style={{ background: `${status.color}22`, color: status.color, border: `1px solid ${status.color}55` }}
                        >
                          {status.label}
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-white/5 rounded-full h-2">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${b.availability}%`, background: `linear-gradient(90deg, ${status.color}, #06b6d4)` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Today's shipments */}
          <div className="border border-white/10 rounded-lg bg-white/[0.02] overflow-hidden mb-14">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <Truck size={16} className="text-[#10b981]" />
                <h2 className="h-sans text-[20px] font-medium">Today&rsquo;s auto-generated shipments</h2>
              </div>
              <p className="text-white/60 text-[13px]">Warehouse &rarr; branch, generated by the 05:30 replenishment run</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="px-4 py-3 text-left text-[12px] font-medium text-white/50 uppercase tracking-wider">Branch</th>
                    <th className="px-4 py-3 text-center text-[12px] font-medium text-white/50 uppercase tracking-wider">Items</th>
                    <th className="px-4 py-3 text-center text-[12px] font-medium text-white/50 uppercase tracking-wider">Units</th>
                    <th className="px-4 py-3 text-center text-[12px] font-medium text-white/50 uppercase tracking-wider">Dispatched</th>
                    <th className="px-4 py-3 text-center text-[12px] font-medium text-white/50 uppercase tracking-wider">ETA</th>
                    <th className="px-4 py-3 text-right text-[12px] font-medium text-white/50 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {SHIPMENTS.map((s) => (
                    <tr key={s.branch} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3 text-[13px] font-medium">{s.branch}</td>
                      <td className="px-4 py-3 text-[13px] text-center text-white/60">{s.items}</td>
                      <td className="px-4 py-3 text-[13px] text-center text-white/60">{s.units}</td>
                      <td className="px-4 py-3 text-[13px] text-center text-white/60">{s.dispatched}</td>
                      <td className="px-4 py-3 text-[13px] text-center text-white/60">{s.eta}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end">
                          <StatusPipeline status={s.status} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* System status */}
          <div className="border border-[#10b981]/30 rounded-lg bg-[#10b981]/5 p-8">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="h-sans text-[22px] font-medium mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#10b981]"></span>
                  Replenishment Running On Schedule
                </h3>
                <p className="text-white/60 max-w-[600px]">
                  5 of 6 branches received a shipment this morning &mdash; Depok started the day
                  fully stocked. Zero missed replenishment runs in the last 30 days.
                </p>
              </div>
              <CheckCircle2 className="text-[#10b981] flex-shrink-0" size={40} />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
